/**
 * BrainTestSuite — Regression testing panel for Brain Engine
 * Create test queries with expected keywords and run them against a preset.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../common/contexts/ProjectContext";
import { socketService } from "../../../services/socket";
import "../../discovery/views/Discovery.css";
import { authFetch } from "../../../services/authFetch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TestCaseFilters {
  rundownIds?: string[];
  eventIds?: string[];
  storyIds?: string[];
  participantIds?: string[];
  tags?: string[];
  selectAll?: boolean;
  dataSources?: { logs: boolean; transcripts: boolean };
}

interface TestCase {
  id: string;
  query: string;
  expected_keywords: string[];
  category: string;
  filters: TestCaseFilters | null;
  created_at: string;
}

interface TestResult {
  testCaseId: string;
  query: string;
  category: string;
  passed: boolean;
  keywordResults: { keyword: string; found: boolean }[];
  responseTimeMs: number;
  answerPreview: string;
  citationsCount: number;
  error: string | null;
}

interface TestRun {
  id: string;
  preset_id: string;
  total_cases: number;
  passed: number;
  failed: number;
  run_at: string;
}

interface Preset {
  id: string;
  name: string;
  is_active: boolean;
}

const panelBg = "var(--color-panel-bg)";
const panelBorder = "1px solid var(--color-panel-border)";
const brandColor = "var(--color-brand, #E53E3E)";
const brandRgb = "var(--color-brand-rgb, 229, 62, 62)";
const cardStyle: React.CSSProperties = {
  background: panelBg,
  border: panelBorder,
  borderRadius: "12px",
  padding: "16px",
};

export const BrainTestSuite: React.FC = () => {
  const { t } = useTranslation();
  const { activeProject } = useProjects();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [results, setResults] = useState<TestResult[] | null>(null);
  const [running, setRunning] = useState(false);
  const [runSummary, setRunSummary] = useState<{
    passed: number;
    failed: number;
    total: number;
  } | null>(null);

  // New test case form
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newQuery, setNewQuery] = useState("");
  const [newKeywords, setNewKeywords] = useState("");
  const [newCategory, setNewCategory] = useState("general");

  // Filters for new test case
  const [filtersData, setFiltersData] = useState<{
    rundowns: any[];
    events: any[];
    stories: any[];
    participants: any[];
  }>({ rundowns: [], events: [], stories: [], participants: [] });
  const [newFilters, setNewFilters] = useState<TestCaseFilters>({
    rundownIds: [],
    eventIds: [],
    storyIds: [],
    participantIds: [],
    selectAll: true,
    dataSources: { logs: true, transcripts: true },
  });

  // Load filter data
  useEffect(() => {
    if (!activeProject) return;
    const handler = (data: any) =>
      setFiltersData({
        rundowns: data.rundowns || [],
        events: data.events || [],
        stories: data.stories || [],
        participants: data.participants || [],
      });
    socketService.on("ai:filters-data", handler);
    socketService.emit("ai:get-filters-data", { projectId: activeProject.id });
    return () => {
      socketService.off("ai:filters-data", handler);
    };
  }, [activeProject]);

  const toggleNewFilter = (
    type: "rundownIds" | "eventIds" | "storyIds" | "participantIds",
    id: string,
  ) => {
    setNewFilters((prev) => {
      const arr = prev[type] || [];
      const updated = arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
      return { ...prev, [type]: updated, selectAll: false };
    });
  };

  // Tree expand/collapse state
  const [expandedRundownIds, setExpandedRundownIds] = useState<string[]>([]);
  const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);
  const toggleRundownExpand = (id: string) =>
    setExpandedRundownIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleEventExpand = (id: string) =>
    setExpandedEventIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const loadData = useCallback(async () => {
    if (!activeProject) return;
    try {
      const [presetsRes, casesRes, runsRes] = await Promise.all([
        authFetch(`/api/brain/presets?projectId=${activeProject.id}`),
        authFetch(`/api/brain/test-cases?projectId=${activeProject.id}`),
        authFetch(`/api/brain/test-runs?projectId=${activeProject.id}`),
      ]);
      const presetsData = await presetsRes.json();
      const casesData = await casesRes.json();
      const runsData = await runsRes.json();
      setPresets(presetsData.presets || []);
      setTestCases(casesData.testCases || []);
      setTestRuns(runsData.testRuns || []);
      if (presetsData.presets?.length > 0 && !selectedPreset) {
        setSelectedPreset(presetsData.presets[0].id);
      }
    } catch (e) {
      console.error("Failed to load test suite data", e);
    }
  }, [activeProject]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addTestCase = async () => {
    if (!newQuery.trim() || !activeProject) return;
    const hasFilters =
      !newFilters.selectAll &&
      ((newFilters.rundownIds?.length || 0) > 0 ||
        (newFilters.eventIds?.length || 0) > 0 ||
        (newFilters.storyIds?.length || 0) > 0 ||
        (newFilters.participantIds?.length || 0) > 0);
    try {
      await authFetch("/api/brain/test-cases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId: activeProject.id,
          query: newQuery.trim(),
          expectedKeywords: newKeywords
            .split(",")
            .map((k) => k.trim())
            .filter(Boolean),
          category: newCategory,
          filters: hasFilters
            ? {
                rundownIds: newFilters.rundownIds || [],
                eventIds: newFilters.eventIds || [],
                storyIds: newFilters.storyIds || [],
                participantIds: newFilters.participantIds || [],
                tags: [],
                dataSources: newFilters.dataSources,
              }
            : null,
        }),
      });
      setNewQuery("");
      setNewKeywords("");
      setNewFilters({
        rundownIds: [],
        eventIds: [],
        storyIds: [],
        participantIds: [],
        selectAll: true,
        dataSources: { logs: true, transcripts: true },
      });
      setShowAddForm(false);
      setEditingId(null);
      loadData();
      window.dispatchEvent(new Event("test-cases-updated"));
    } catch (e) {
      console.error("Failed to add test case", e);
    }
  };

  const updateTestCase = async () => {
    if (!newQuery.trim() || !editingId) return;
    const hasFilters =
      !newFilters.selectAll &&
      ((newFilters.rundownIds?.length || 0) > 0 ||
        (newFilters.eventIds?.length || 0) > 0 ||
        (newFilters.storyIds?.length || 0) > 0 ||
        (newFilters.participantIds?.length || 0) > 0);
    try {
      await authFetch(`/api/brain/test-cases/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: newQuery.trim(),
          expectedKeywords: newKeywords
            .split(",")
            .map((k) => k.trim())
            .filter(Boolean),
          category: newCategory,
          filters: hasFilters
            ? {
                rundownIds: newFilters.rundownIds || [],
                eventIds: newFilters.eventIds || [],
                storyIds: newFilters.storyIds || [],
                participantIds: newFilters.participantIds || [],
                tags: [],
                dataSources: newFilters.dataSources,
              }
            : null,
        }),
      });
      setNewQuery("");
      setNewKeywords("");
      setNewFilters({
        rundownIds: [],
        eventIds: [],
        storyIds: [],
        participantIds: [],
        selectAll: true,
        dataSources: { logs: true, transcripts: true },
      });
      setShowAddForm(false);
      setEditingId(null);
      loadData();
      window.dispatchEvent(new Event("test-cases-updated"));
    } catch (e) {
      console.error("Failed to update test case", e);
    }
  };

  const editTestCase = (tc: TestCase) => {
    setEditingId(tc.id);
    setNewQuery(tc.query);
    setNewKeywords((tc.expected_keywords || []).join(", "));
    setNewCategory(tc.category || "general");
    if (tc.filters) {
      setNewFilters({
        rundownIds: tc.filters.rundownIds || [],
        eventIds: tc.filters.eventIds || [],
        storyIds: tc.filters.storyIds || [],
        participantIds: tc.filters.participantIds || [],
        selectAll: false,
        dataSources: tc.filters.dataSources || { logs: true, transcripts: true },
      });
    } else {
      setNewFilters({
        rundownIds: [],
        eventIds: [],
        storyIds: [],
        participantIds: [],
        selectAll: true,
        dataSources: { logs: true, transcripts: true },
      });
    }
    setShowAddForm(true);
  };

  const deleteTestCase = async (id: string) => {
    try {
      await authFetch(`/api/brain/test-cases/${id}`, { method: "DELETE" });
      loadData();
      window.dispatchEvent(new Event("test-cases-updated"));
    } catch (e) {
      console.error("Failed to delete test case", e);
    }
  };

  const runSuite = async () => {
    if (!activeProject || !selectedPreset || testCases.length === 0) return;
    setRunning(true);
    setResults(null);
    setRunSummary(null);
    try {
      const res = await authFetch("/api/brain/test-suite/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: activeProject.id, presetId: selectedPreset }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResults(data.results || []);
      setRunSummary({ passed: data.passed, failed: data.failed, total: data.totalCases });
      loadData();
    } catch (err: any) {
      console.error("Test suite error:", err);
    } finally {
      setRunning(false);
    }
  };

  const getPresetName = (id: string) =>
    presets.find((p) => p.id === id)?.name || id.substring(0, 8);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        height: "100%",
        overflow: "auto",
      }}
    >
      {/* Header bar */}
      <div
        style={{ ...cardStyle, display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}
      >
        <i className="bx bx-check-shield" style={{ fontSize: "1.3rem", color: brandColor }}></i>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "0.85rem", fontWeight: 800, color: "#ddd" }}>
            {t("admin.brainTestSuite.regressionTestSuite")}
          </div>
          <div style={{ fontSize: "0.68rem", color: "#666" }}>
            {t("admin.brainTestSuite.testCasesCount", {
              cases: testCases.length,
              runs: testRuns.length,
            })}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Select
            value={selectedPreset}
            onValueChange={(val) => {
              if (val) setSelectedPreset(val);
            }}
          >
            <SelectTrigger size="sm" className="w-[180px]">
              <SelectValue placeholder="Select preset..." />
            </SelectTrigger>
            <SelectContent>
              {presets.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={runSuite}
            disabled={running || testCases.length === 0}
            style={{
              background: running ? "rgba(255,255,255,0.05)" : `rgba(${brandRgb}, 0.2)`,
              border: `1px solid ${running ? "rgba(255,255,255,0.1)" : `rgba(${brandRgb}, 0.4)`}`,
              color: running ? "#888" : brandColor,
              borderRadius: "8px",
              padding: "8px 20px",
              fontSize: "0.78rem",
              fontWeight: 800,
              cursor: running ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
              transition: "all 0.2s",
            }}
          >
            <i
              className={`bx ${running ? "bx-loader-alt bx-spin" : "bx-play"}`}
              style={{ fontSize: "1rem" }}
            ></i>
            {running
              ? t("admin.brainTestSuite.executing")
              : t("admin.brainTestSuite.executeBattery")}
          </button>
        </div>
      </div>

      {/* Run summary */}
      {runSummary && (
        <div
          style={{
            ...cardStyle,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "20px",
            borderColor: runSummary.failed === 0 ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)",
          }}
        >
          <div
            style={{
              fontSize: "2rem",
              fontWeight: 900,
              color: runSummary.failed === 0 ? "#22c55e" : "#ef4444",
            }}
          >
            {runSummary.failed === 0 ? (
              <i className="bx bx-check-circle"></i>
            ) : (
              <i className="bx bx-x-circle"></i>
            )}
          </div>
          <div>
            <div
              style={{
                fontSize: "0.85rem",
                fontWeight: 800,
                color: runSummary.failed === 0 ? "#22c55e" : "#ef4444",
              }}
            >
              {runSummary.failed === 0
                ? t("admin.brainTestSuite.allTestsPassed")
                : t("admin.brainTestSuite.testsFailed", { count: runSummary.failed })}
            </div>
            <div style={{ fontSize: "0.72rem", color: "#888" }}>
              {t("admin.brainTestSuite.passedOf", {
                passed: runSummary.passed,
                total: runSummary.total,
              })}
              {" | Preset: "}
              {getPresetName(selectedPreset)}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {/* Progress bar */}
          <div style={{ width: "120px" }}>
            <div
              style={{
                height: "6px",
                borderRadius: "3px",
                background: "rgba(255,255,255,0.06)",
                overflow: "hidden",
                display: "flex",
              }}
            >
              <div
                style={{
                  width: `${(runSummary.passed / runSummary.total) * 100}%`,
                  background: "#22c55e",
                  height: "100%",
                }}
              />
              <div
                style={{
                  width: `${(runSummary.failed / runSummary.total) * 100}%`,
                  background: "#ef4444",
                  height: "100%",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Test results */}
      {results && (
        <div style={{ ...cardStyle, flexShrink: 0 }}>
          <h4
            style={{
              margin: "0 0 10px 0",
              fontSize: "0.7rem",
              fontWeight: 800,
              letterSpacing: "1px",
              color: "#888",
            }}
          >
            {t("admin.brainTestSuite.results")}
          </h4>
          {results.map((r, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "10px 0",
                borderBottom: i < results.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: r.passed ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                  color: r.passed ? "#22c55e" : "#ef4444",
                  fontSize: "0.9rem",
                  flexShrink: 0,
                }}
              >
                <i className={`bx ${r.passed ? "bx-check" : "bx-x"}`}></i>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "0.75rem", color: "#ddd", fontWeight: 600 }}>{r.query}</div>
                {r.error && (
                  <div style={{ fontSize: "0.68rem", color: "#ef4444", marginTop: "2px" }}>
                    Error: {r.error}
                  </div>
                )}
                {r.keywordResults.length > 0 && (
                  <div style={{ display: "flex", gap: "4px", marginTop: "4px", flexWrap: "wrap" }}>
                    {r.keywordResults.map((kr, ki) => (
                      <span
                        key={ki}
                        style={{
                          fontSize: "0.6rem",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          background: kr.found ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                          border: `1px solid ${kr.found ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`,
                          color: kr.found ? "#22c55e" : "#ef4444",
                        }}
                      >
                        {kr.found ? "" : ""} {kr.keyword}
                      </span>
                    ))}
                  </div>
                )}
                {r.answerPreview && (
                  <div
                    style={{
                      fontSize: "0.65rem",
                      color: "#666",
                      marginTop: "4px",
                      lineHeight: "1.4",
                    }}
                  >
                    {r.answerPreview.substring(0, 120)}...
                  </div>
                )}
              </div>
              <div style={{ fontSize: "0.65rem", color: "#666", whiteSpace: "nowrap" }}>
                {(r.responseTimeMs / 1000).toFixed(1)}s<div>{r.citationsCount} citas</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Test cases list */}
      <div style={{ ...cardStyle, flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "0.7rem",
              fontWeight: 800,
              letterSpacing: "1px",
              color: "#888",
            }}
          >
            TEST CASES
          </h4>
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (showAddForm) {
                setEditingId(null);
                setNewQuery("");
                setNewKeywords("");
                setNewCategory("general");
                setNewFilters({
                  rundownIds: [],
                  eventIds: [],
                  storyIds: [],
                  participantIds: [],
                  selectAll: true,
                  dataSources: { logs: true, transcripts: true },
                });
              }
            }}
            style={{
              background: `rgba(${brandRgb}, 0.1)`,
              border: `1px solid rgba(${brandRgb}, 0.2)`,
              color: brandColor,
              borderRadius: "6px",
              padding: "4px 12px",
              fontSize: "0.7rem",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <i className="bx bx-plus"></i> Añadir
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "10px",
              padding: "14px",
              marginBottom: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <input
              type="text"
              value={newQuery}
              onChange={(e) => setNewQuery(e.target.value)}
              placeholder="Pregunta del test..."
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#fff",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "0.78rem",
                fontFamily: "inherit",
                outline: "none",
              }}
            />
            <input
              type="text"
              value={newKeywords}
              onChange={(e) => setNewKeywords(e.target.value)}
              placeholder="Keywords esperadas (separadas por coma)"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#fff",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "0.78rem",
                fontFamily: "inherit",
                outline: "none",
              }}
            />
            <div style={{ display: "flex", gap: "8px" }}>
              <Select
                value={newCategory}
                onValueChange={(val) => {
                  if (val) setNewCategory(val);
                }}
              >
                <SelectTrigger size="sm" className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="participantes">Participantes</SelectItem>
                  <SelectItem value="eventos">Eventos</SelectItem>
                  <SelectItem value="narrativa">Narrativa</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter selectors */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.05)",
                borderRadius: "8px",
                padding: "10px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}
              >
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 800,
                    letterSpacing: "0.5px",
                    color: "#888",
                  }}
                >
                  {t("admin.brainTestSuite.contextFilters")}
                </span>
                <button
                  onClick={() =>
                    setNewFilters((prev) => ({
                      ...prev,
                      selectAll: !prev.selectAll,
                      rundownIds: [],
                      eventIds: [],
                      storyIds: [],
                      participantIds: [],
                    }))
                  }
                  style={{
                    fontSize: "0.6rem",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    background: newFilters.selectAll
                      ? "rgba(255, 77, 141, 0.15)"
                      : "rgba(255,255,255,0.05)",
                    border: `1px solid ${newFilters.selectAll ? "rgba(255, 77, 141, 0.4)" : "rgba(255,255,255,0.1)"}`,
                    color: newFilters.selectAll ? "var(--discovery-accent, #ff4d8d)" : "#888",
                  }}
                >
                  {newFilters.selectAll ? "✓ TODO" : "SELECT ALL"}
                </button>
                <div style={{ flex: 1 }} />
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    onClick={() =>
                      setNewFilters((prev) => ({
                        ...prev,
                        dataSources: { ...prev.dataSources!, logs: !prev.dataSources!.logs },
                      }))
                    }
                    style={{
                      fontSize: "0.6rem",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: newFilters.dataSources?.logs
                        ? "rgba(59,130,246,0.15)"
                        : "rgba(255,255,255,0.03)",
                      border: `1px solid ${newFilters.dataSources?.logs ? "rgba(59,130,246,0.3)" : "rgba(255,255,255,0.08)"}`,
                      color: newFilters.dataSources?.logs ? "#3b82f6" : "#666",
                    }}
                  >
                    {t("admin.brainTestSuite.logs")}
                  </button>
                  <button
                    onClick={() =>
                      setNewFilters((prev) => ({
                        ...prev,
                        dataSources: {
                          ...prev.dataSources!,
                          transcripts: !prev.dataSources!.transcripts,
                        },
                      }))
                    }
                    style={{
                      fontSize: "0.6rem",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: newFilters.dataSources?.transcripts
                        ? "rgba(168,85,247,0.15)"
                        : "rgba(255,255,255,0.03)",
                      border: `1px solid ${newFilters.dataSources?.transcripts ? "rgba(168,85,247,0.3)" : "rgba(255,255,255,0.08)"}`,
                      color: newFilters.dataSources?.transcripts ? "#a855f7" : "#666",
                    }}
                  >
                    {t("admin.brainTestSuite.trans")}
                  </button>
                </div>
              </div>

              {!newFilters.selectAll && (
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  {/* HIERARCHICAL TREE: Rundown → Event → Story */}
                  <div style={{ flex: 2, minWidth: "250px" }}>
                    <div
                      style={{
                        fontSize: "0.6rem",
                        fontWeight: 800,
                        color: "#777",
                        marginBottom: "6px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {t("admin.brainTestSuite.rundownsStories")}
                    </div>
                    <div
                      className="tree-container"
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      {filtersData.rundowns.map((r) => {
                        const isRundownActive = (newFilters.rundownIds || []).includes(r.id);
                        return (
                          <div key={r.id} className="tree-item">
                            <div
                              className={`tree-row tree-row-rundown${isRundownActive ? " tree-row-active" : ""}`}
                              onClick={() => toggleRundownExpand(r.id)}
                            >
                              <i
                                className={`bx bx-chevron-${expandedRundownIds.includes(r.id) ? "down" : "right"} tree-expand-icon`}
                              />
                              <input
                                type="checkbox"
                                className="tree-checkbox tree-checkbox-rundown"
                                checked={isRundownActive}
                                onChange={() => toggleNewFilter("rundownIds", r.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <i className="bx bx-calendar tree-type-icon tree-icon-rundown" />
                              <span className="tree-label">{r.name}</span>
                            </div>
                            {expandedRundownIds.includes(r.id) && (
                              <div className="tree-children tree-children-rundown">
                                {filtersData.events
                                  .filter((e: any) => e.rundownId === r.id)
                                  .map((evt: any) => {
                                    const isEventActive = (newFilters.eventIds || []).includes(
                                      evt.id,
                                    );
                                    return (
                                      <div key={evt.id} className="tree-item">
                                        <div
                                          className={`tree-row tree-row-event${isEventActive ? " tree-row-active" : ""}`}
                                          onClick={() => toggleEventExpand(evt.id)}
                                        >
                                          <i
                                            className={`bx bx-chevron-${expandedEventIds.includes(evt.id) ? "down" : "right"} tree-expand-icon`}
                                          />
                                          <input
                                            type="checkbox"
                                            className="tree-checkbox tree-checkbox-event"
                                            checked={isEventActive}
                                            onChange={() => toggleNewFilter("eventIds", evt.id)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <i className="bx bx-bookmark tree-type-icon tree-icon-event" />
                                          <span className="tree-label">
                                            {evt.title || evt.name}
                                          </span>
                                        </div>
                                        {expandedEventIds.includes(evt.id) && (
                                          <div className="tree-children tree-children-event">
                                            {filtersData.stories
                                              .filter((s: any) => s.rundownId === evt.id)
                                              .map((st: any) => {
                                                const isStoryActive = (
                                                  newFilters.storyIds || []
                                                ).includes(st.id);
                                                return (
                                                  <div
                                                    key={st.id}
                                                    className={`tree-row tree-row-story${isStoryActive ? " tree-row-active" : ""}`}
                                                  >
                                                    <div style={{ width: "14px" }} />
                                                    <input
                                                      type="checkbox"
                                                      className="tree-checkbox tree-checkbox-story"
                                                      checked={isStoryActive}
                                                      onChange={() =>
                                                        toggleNewFilter("storyIds", st.id)
                                                      }
                                                    />
                                                    <i className="bx bx-book-open tree-type-icon tree-icon-story" />
                                                    <span className="tree-label">{st.title}</span>
                                                  </div>
                                                );
                                              })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* PARTICIPANTS */}
                  {filtersData.participants.length > 0 && (
                    <div style={{ flex: 1, minWidth: "180px" }}>
                      <div
                        style={{
                          fontSize: "0.6rem",
                          fontWeight: 800,
                          color: "#777",
                          marginBottom: "6px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("admin.brainTestSuite.participantsLabel")}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {filtersData.participants.map((p) => {
                          const isActive = (newFilters.participantIds || []).includes(p.id);
                          const name = p.name || p.alias || "Sin nombre";
                          return (
                            <div
                              key={p.id}
                              onClick={() => toggleNewFilter("participantIds", p.id)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 6px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "0.65rem",
                                background: isActive ? "rgba(59,130,246,0.15)" : "transparent",
                                border: isActive
                                  ? "1px solid rgba(59,130,246,0.3)"
                                  : "1px solid transparent",
                                color: isActive ? "#3b82f6" : "#999",
                              }}
                            >
                              <i
                                className={`bx ${isActive ? "bx-check-square" : "bx-square"}`}
                                style={{ fontSize: "0.75rem" }}
                              />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Active Filters Preview Box (New Test Case) */}
            <div
              style={{
                marginTop: "15px",
                padding: "12px 16px",
                background: "var(--color-bg-tertiary, rgba(20,20,25,0.95))",
                borderTop: "1px solid var(--color-border, rgba(255,255,255,0.05))",
                borderBottom: "1px solid var(--color-border, rgba(255,255,255,0.05))",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  color: "#888",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                <i
                  className="bx bx-filter-alt"
                  style={{ color: "var(--color-accent-success, #10b981)" }}
                ></i>
                <span>{t("admin.brainTestSuite.activeFilters")}</span>
                {!(
                  newFilters.rundownIds?.length ||
                  newFilters.eventIds?.length ||
                  newFilters.storyIds?.length ||
                  newFilters.participantIds?.length ||
                  newFilters.tags?.length
                ) &&
                  !newFilters.selectAll && (
                    <span
                      style={{
                        opacity: 0.5,
                        marginLeft: "4px",
                        fontStyle: "italic",
                        fontWeight: 400,
                      }}
                    >
                      {t("admin.brainTestSuite.noFiltersApplied")}
                    </span>
                  )}
                {newFilters.selectAll && (
                  <span
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "var(--discovery-accent, #ff4d8d)",
                      background: "rgba(255, 77, 141, 0.15)",
                      border: "1px solid rgba(255, 77, 141, 0.4)",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("admin.brainTestSuite.allProject")}
                  </span>
                )}
              </div>
              {!newFilters.selectAll &&
              (newFilters.rundownIds?.length ||
                newFilters.eventIds?.length ||
                newFilters.storyIds?.length ||
                newFilters.participantIds?.length ||
                newFilters.tags?.length) ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {newFilters.rundownIds?.map((id) => {
                    const r = filtersData.rundowns.find((x) => x.id === id);
                    return r ? (
                      <span
                        key={`ar-${id}`}
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: "#f59e0b",
                          background: "rgba(245,158,11,0.15)",
                          border: "1px solid rgba(245,158,11,0.4)",
                          padding: "3px 8px",
                          borderRadius: "12px",
                          textTransform: "uppercase",
                        }}
                      >
                        {r.name}
                      </span>
                    ) : null;
                  })}
                  {newFilters.eventIds?.map((id) => {
                    const e = filtersData.events.find((x) => x.id === id);
                    return e ? (
                      <span
                        key={`ae-${id}`}
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: "#38bdf8",
                          background: "rgba(56,189,248,0.15)",
                          border: "1px solid rgba(56,189,248,0.4)",
                          padding: "3px 8px",
                          borderRadius: "12px",
                          textTransform: "uppercase",
                        }}
                      >
                        {e.title || e.name}
                      </span>
                    ) : null;
                  })}
                  {newFilters.storyIds?.map((id) => {
                    const s = filtersData.stories.find((x) => x.id === id);
                    return s ? (
                      <span
                        key={`as-${id}`}
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: "#10b981",
                          background: "rgba(16,185,129,0.15)",
                          border: "1px solid rgba(16,185,129,0.4)",
                          padding: "3px 8px",
                          borderRadius: "12px",
                          textTransform: "uppercase",
                        }}
                      >
                        {s.title}
                      </span>
                    ) : null;
                  })}
                  {newFilters.participantIds?.map((id) => {
                    const p = filtersData.participants.find((x) => x.id === id);
                    return p ? (
                      <span
                        key={`ap-${id}`}
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: "#ec4899",
                          background: "rgba(236,72,153,0.15)",
                          border: "1px solid rgba(236,72,153,0.4)",
                          padding: "3px 8px",
                          borderRadius: "12px",
                          textTransform: "uppercase",
                        }}
                      >
                        {p.alias || p.name}
                      </span>
                    ) : null;
                  })}
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              {editingId && (
                <button
                  onClick={() => {
                    setEditingId(null);
                    setNewQuery("");
                    setNewKeywords("");
                    setNewCategory("general");
                    setNewFilters({
                      rundownIds: [],
                      eventIds: [],
                      storyIds: [],
                      participantIds: [],
                      selectAll: true,
                      dataSources: { logs: true, transcripts: true },
                    });
                    setShowAddForm(false);
                  }}
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#888",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {t("admin.brainTestSuite.cancel")}
                </button>
              )}
              <button
                onClick={editingId ? updateTestCase : addTestCase}
                disabled={!newQuery.trim()}
                style={{
                  background: `rgba(${brandRgb}, 0.15)`,
                  border: `1px solid rgba(${brandRgb}, 0.3)`,
                  color: brandColor,
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <i
                  className={`bx ${editingId ? "bx-edit" : "bx-save"}`}
                  style={{ marginRight: "4px" }}
                />
                {editingId
                  ? t("admin.brainTestSuite.updateTestCase")
                  : t("admin.brainTestSuite.saveTestCase")}
              </button>
            </div>
          </div>
        )}

        {/* Cases list */}
        {testCases.length === 0 ? (
          <div
            style={{ color: "#555", fontSize: "0.75rem", textAlign: "center", padding: "30px 0" }}
          >
            {t("admin.brainTestSuite.noTestCases")}
          </div>
        ) : (
          testCases.map((tc) => (
            <div
              key={tc.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "8px 0",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "0.75rem", color: "#ccc", fontWeight: 600 }}>
                  {tc.query}
                </div>
                <div style={{ display: "flex", gap: "4px", marginTop: "3px", flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontSize: "0.6rem",
                      padding: "1px 6px",
                      borderRadius: "4px",
                      background: "rgba(139,92,246,0.1)",
                      border: "1px solid rgba(139,92,246,0.2)",
                      color: "#a78bfa",
                    }}
                  >
                    {tc.category}
                  </span>
                  {tc.expected_keywords?.map((kw, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: "0.6rem",
                        padding: "1px 6px",
                        borderRadius: "4px",
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        color: "#888",
                      }}
                    >
                      {kw}
                    </span>
                  ))}
                  {tc.filters ? (
                    <>
                      <span
                        style={{
                          fontSize: "0.6rem",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          background: "rgba(212,168,66,0.1)",
                          border: "1px solid rgba(212,168,66,0.2)",
                          color: "#D4A842",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <i
                          className="bx bx-filter-alt"
                          style={{ fontSize: "0.6rem", marginRight: "4px" }}
                        />
                        {[
                          (tc.filters.rundownIds?.length || 0) > 0
                            ? `${tc.filters.rundownIds!.length} rnd`
                            : "",
                          (tc.filters.eventIds?.length || 0) > 0
                            ? `${tc.filters.eventIds!.length} evt`
                            : "",
                          (tc.filters.storyIds?.length || 0) > 0
                            ? `${tc.filters.storyIds!.length} story`
                            : "",
                          (tc.filters.participantIds?.length || 0) > 0
                            ? `${tc.filters.participantIds!.length} part`
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" + ")}
                      </span>
                      {/* Render explicitly selected filter names with the new style */}
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          flexWrap: "wrap",
                          marginTop: "4px",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <i
                          className="bx bx-filter-alt"
                          style={{ fontSize: "0.65rem", color: "#888", marginRight: "2px" }}
                        />
                        {tc.filters.rundownIds?.map((id) => {
                          const r = filtersData.rundowns.find((x) => x.id === id);
                          return r ? (
                            <span
                              key={`r-${id}`}
                              style={{
                                fontSize: "0.55rem",
                                fontWeight: 700,
                                color: "#f59e0b",
                                background: "rgba(245,158,11,0.15)",
                                border: "1px solid rgba(245,158,11,0.4)",
                                padding: "2px 6px",
                                borderRadius: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {r.name}
                            </span>
                          ) : null;
                        })}
                        {tc.filters.eventIds?.map((id) => {
                          const e = filtersData.events.find((x) => x.id === id);
                          return e ? (
                            <span
                              key={`e-${id}`}
                              style={{
                                fontSize: "0.55rem",
                                fontWeight: 700,
                                color: "#38bdf8",
                                background: "rgba(56,189,248,0.15)",
                                border: "1px solid rgba(56,189,248,0.4)",
                                padding: "2px 6px",
                                borderRadius: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {e.title || e.name}
                            </span>
                          ) : null;
                        })}
                        {tc.filters.storyIds?.map((id) => {
                          const s = filtersData.stories.find((x) => x.id === id);
                          return s ? (
                            <span
                              key={`s-${id}`}
                              style={{
                                fontSize: "0.55rem",
                                fontWeight: 700,
                                color: "#10b981",
                                background: "rgba(16,185,129,0.15)",
                                border: "1px solid rgba(16,185,129,0.4)",
                                padding: "2px 6px",
                                borderRadius: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {s.title}
                            </span>
                          ) : null;
                        })}
                        {tc.filters.participantIds?.map((id) => {
                          const p = filtersData.participants.find((x) => x.id === id);
                          return p ? (
                            <span
                              key={`p-${id}`}
                              style={{
                                fontSize: "0.55rem",
                                fontWeight: 700,
                                color: "#ec4899",
                                background: "rgba(236,72,153,0.15)",
                                border: "1px solid rgba(236,72,153,0.4)",
                                padding: "2px 6px",
                                borderRadius: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {p.name || p.alias}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </>
                  ) : (
                    <span
                      style={{
                        fontSize: "0.6rem",
                        padding: "1px 6px",
                        borderRadius: "4px",
                        background: "rgba(255, 77, 141, 0.08)",
                        border: "1px solid rgba(255, 77, 141, 0.15)",
                        color: "rgba(255, 77, 141, 0.6)",
                      }}
                    >
                      ALL
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                <button
                  onClick={() => editTestCase(tc)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#666",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    padding: "4px",
                  }}
                  title="Editar"
                >
                  <i className="bx bx-edit-alt"></i>
                </button>
                <button
                  onClick={() => deleteTestCase(tc.id)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#555",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    padding: "4px",
                  }}
                  title="Eliminar"
                >
                  <i className="bx bx-trash"></i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Run history */}
      {testRuns.length > 0 && (
        <div style={{ ...cardStyle, flexShrink: 0 }}>
          <h4
            style={{
              margin: "0 0 10px 0",
              fontSize: "0.7rem",
              fontWeight: 800,
              letterSpacing: "1px",
              color: "#888",
            }}
          >
            {t("admin.brainTestSuite.results")}
          </h4>
          {testRuns.map((run) => (
            <div
              key={run.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "6px 0",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
                fontSize: "0.72rem",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: run.failed === 0 ? "#22c55e" : "#ef4444",
                }}
              />
              <div style={{ color: "#aaa", flex: 1 }}>
                {t("admin.brainTestSuite.passedOf", { passed: run.passed, total: run.total_cases })}
              </div>
              <div style={{ color: "#666" }}>{getPresetName(run.preset_id)}</div>
              <div style={{ color: "#555" }}>
                {new Date(run.run_at).toLocaleDateString("es-ES", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
